// src/components/ClientsPage/ClientsPage.tsx

import React, { useCallback, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";

import { RootState } from "../../redux/store";

import { setClientId } from "../../redux/userSlice";

import api from "../../api/client";

import "./ClientsPage.css";

type MaidStatus = "not_arrived" | "arrived";

type ClientRow = {
  client_id: number;

  client_name: string;

  contact_info: string;

  maid_id: number | null;

  maid_name: string | null;

  agent_name: string | null;

  status: MaidStatus | null;

  arrival_date: string | null;
};

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const email = useSelector((state: RootState) => state.user.email);

  const token = useSelector((state: RootState) => state.user.token);

  const [clients, setClients] = useState<ClientRow[]>([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  /* ============================================================
     STATUS
  ============================================================ */

  const displayStatus = (status: MaidStatus | null) => {
    if (status === "arrived") {
      return "Arrived";
    }

    if (status === "not_arrived") {
      return "Have Not Arrived";
    }

    return "—";
  };

  /* ============================================================
     SORT CLIENTS BY ARRIVAL DATE

     Oldest arrival date first
     Newest arrival date last
     No arrival date at the bottom
  ============================================================ */

  const sortClients = (rows: ClientRow[]) => {
    return [...rows].sort((a, b) => {
      if (!a.arrival_date && !b.arrival_date) {
        return 0;
      }

      if (!a.arrival_date) {
        return 1;
      }

      if (!b.arrival_date) {
        return -1;
      }

      return (
        new Date(a.arrival_date).getTime() - new Date(b.arrival_date).getTime()
      );
    });
  };

  /* ============================================================
     LOAD ALL CLIENTS
  ============================================================ */

  const loadClients = useCallback(async () => {
    if (!token) {
      setError("Your session has expired. Please sign in again.");

      return;
    }

    try {
      setLoading(true);

      setError("");

      /*
       * Your existing /search endpoint is used here.
       *
       * An empty client_name query requests the full list.
       */

      const response = await api.get("/search", {
        params: {
          field: "client_name",
          query: "",
        },

        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const rows = (response.data.results || []) as ClientRow[];

      setClients(sortClients(rows));
    } catch (err: any) {
      console.error("Error loading clients:", err);

      setClients([]);

      if (err?.response?.status === 401) {
        setError(
          "Your session has expired. Please sign out and sign in again."
        );
      } else {
        setError(err?.response?.data?.message || "Unable to load clients.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  /* ============================================================
     LOAD COMPLETE TABLE WHEN CLIENTS PAGE OPENS
  ============================================================ */

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  /* ============================================================
     OPEN CLIENT
  ============================================================ */

  const handleClientClick = (clientId: number) => {
    dispatch(setClientId(clientId));

    navigate(`/edit-client/${clientId}`);
  };

  /* ============================================================
     PAGE
  ============================================================ */

  return (
    <div className="clients-page">
      <h1>Clients Management</h1>

      <p className={email ? "logged-in" : "not-logged-in"}>
        {email ? `Logged in as: ${email}` : "Not logged in"}
      </p>

      {/* ========================================================
          CLIENT MANAGEMENT BUTTONS
      ======================================================== */}

      <div className="button-group">
        <button type="button" onClick={() => navigate("/create-client")}>
          Add New Client
        </button>

        <button type="button" onClick={() => navigate("/search")}>
          Search
        </button>
      </div>

      {/* ========================================================
          COMPLETE CLIENT LIST
      ======================================================== */}

      <div className="clients-list-section">
        <h2>All Clients</h2>

        {loading && <p>Loading clients...</p>}

        {error && <p className="error-message">{error}</p>}

        {!loading && !error && clients.length === 0 && <p>No clients found.</p>}

        {!loading && clients.length > 0 && (
          <div className="clients-table-wrapper">
            <table className="clients-table">
              <thead>
                <tr>
                  <th>No.</th>

                  <th>Client</th>

                  <th>Contact Info</th>

                  <th>Maid</th>

                  <th>Agent</th>

                  <th>Arrival Status</th>

                  <th>Arrival Date</th>
                </tr>
              </thead>

              <tbody>
                {clients.map((client, index) => (
                  <tr
                    key={
                      client.maid_id !== null
                        ? `${client.client_id}-${client.maid_id}`
                        : `${client.client_id}-${index}`
                    }
                    onClick={() => handleClientClick(client.client_id)}
                  >
                    <td>{index + 1}</td>

                    <td>{client.client_name}</td>

                    <td>{client.contact_info}</td>

                    <td>{client.maid_name || "—"}</td>

                    <td>{client.agent_name || "—"}</td>

                    <td>
                      <span
                        className={
                          client.status === "arrived"
                            ? "status-badge status-arrived"
                            : client.status === "not_arrived"
                            ? "status-badge status-not-arrived"
                            : "status-badge"
                        }
                      >
                        {displayStatus(client.status)}
                      </span>
                    </td>

                    <td>{client.arrival_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
